<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { createKfAccount, updateKfAccount } from '@/api/weworkKf'
import { compressImage } from '@/utils/imageCompress'

const props = defineProps({
  show: Boolean,
  isEdit: Boolean,
  accountData: Object
})

const emit = defineEmits(['update:show', 'success', 'error'])

// 表单数据
const form = reactive({
  id: null,
  name: ''
})

// 头像相关
const avatarInput = ref()
const pendingAvatarFile = ref(null)
const avatarPreview = ref('')
const existingAvatar = ref('')

// 提交 loading
const submitting = ref(false)

// 监听显示/隐藏
watch(() => props.show, (val) => {
  if (val) {
    if (props.isEdit && props.accountData) {
      form.id = props.accountData.id
      form.name = props.accountData.name || ''
      existingAvatar.value = props.accountData.avatar || ''
      pendingAvatarFile.value = null
      avatarPreview.value = ''
    } else {
      resetForm()
    }
  }
})

const resetForm = () => {
  form.id = null
  form.name = ''
  existingAvatar.value = ''
  pendingAvatarFile.value = null
  avatarPreview.value = ''
}

// 头像选择
const triggerAvatarUpload = () => avatarInput.value?.click()

const handleAvatarChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过5MB')
    return
  }

  const compressed = await compressImage(file)
  pendingAvatarFile.value = compressed
  avatarPreview.value = URL.createObjectURL(compressed)
  existingAvatar.value = ''
  event.target.value = ''
}

const removeAvatar = () => {
  existingAvatar.value = ''
  pendingAvatarFile.value = null
  avatarPreview.value = ''
}

// 当前显示的头像
const displayAvatar = () => {
  return avatarPreview.value || existingAvatar.value
}

// 关闭
const handleClose = () => {
  emit('update:show', false)
}

// 提交
const handleSubmit = async () => {
  if (!form.name.trim()) {
    return ElMessage.warning('请输入客服名称')
  }

  if (!props.isEdit && !pendingAvatarFile.value) {
    return ElMessage.warning('请上传客服头像')
  }

  submitting.value = true
  try {
    if (props.isEdit) {
      await updateKfAccount(form.id, {
        name: form.name.trim(),
        avatarFile: pendingAvatarFile.value || undefined
      })
      ElMessage.success('更新成功')
    } else {
      await createKfAccount(form.name.trim(), pendingAvatarFile.value)
      ElMessage.success('创建成功')
    }
    emit('success')
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
    emit('error')
  } finally {
    submitting.value = false
  }
}

const done = () => {
  submitting.value = false
  handleClose()
}

defineExpose({ done })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full flex flex-col">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 class="text-lg font-semibold text-slate-900">
                {{ isEdit ? '编辑客服账号' : '新增客服账号' }}
              </h3>
              <button
                @click="handleClose"
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 内容 -->
            <div class="p-6 space-y-5">
              <!-- 头像上传 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">
                  客服头像 <span v-if="!isEdit" class="text-red-500">*</span>
                </label>
                <div class="flex items-center gap-4">
                  <div v-if="displayAvatar()" class="relative group">
                    <img
                      :src="displayAvatar()"
                      alt="客服头像"
                      class="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                    />
                    <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center gap-1">
                      <button
                        @click="triggerAvatarUpload"
                        class="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors cursor-pointer"
                        title="更换"
                      >
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </button>
                      <button
                        @click="removeAvatar"
                        class="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white hover:bg-red-500/80 transition-colors cursor-pointer"
                        title="移除"
                      >
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div
                    v-else
                    @click="triggerAvatarUpload"
                    class="w-16 h-16 border-2 border-dashed border-slate-300 rounded-full flex flex-col items-center justify-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                  >
                    <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v16m8-8H4"/>
                    </svg>
                  </div>
                  <div class="text-xs text-slate-400">
                    <p>支持 JPG/PNG 格式</p>
                    <p>不超过 5MB</p>
                  </div>
                </div>
                <input
                  ref="avatarInput"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleAvatarChange"
                />
              </div>

              <!-- 客服名称 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">
                  客服名称 <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  maxlength="30"
                  placeholder="请输入客服名称"
                  class="form-input w-full"
                />
                <p class="mt-1 text-xs text-slate-400">{{ form.name.length }}/30</p>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
              <button
                @click="handleClose"
                class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                @click="handleSubmit"
                :disabled="submitting"
                class="btn-primary"
              >
                {{ submitting ? '提交中...' : '确定' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
